import { CaretLeft, CaretRight } from '@carbon/icons-react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination, A11y, Mousewheel } from 'swiper'
import { useMediaQuery } from '@uidotdev/usehooks'
import { Tile } from 'carbon-components-react'
import { useEffect, useState, useRef } from 'react'
import cx from 'classnames'
import watsonAssistant from './utils/watson-assistant' // Ensure this is the correct path
import ImageModal from './components/carousel/imageModal'
import playButton from './assets/play_button.svg'

function ContentCarouselGallery({}) {
  const isPhoneScreen = useMediaQuery('screen and (max-width: 1080px)')
  const instance = watsonAssistant.getInstance() // Ensure watsonAssistant is properly imported and has a `getInstance` method
  const [navigationElement, setNavigationElement] = useState()
  const [carouselItems, setCarouselItems] = useState([])
  const [loading, setLoading] = useState(true)

  const [modalOpen, setModalOpen] = useState(false)
  const [initialModalIndex, setInitialModalIndex] = useState(0)

  const videoRefs = useRef({})

  const BASE_URL = process.env.REACT_APP_BASE_URL
  // const baseURL = "http://localhost:8000/";
  const baseURL = 'https://backend-dev.1qo1qzj3a6fc.ca-tor.codeengine.appdomain.cloud/'
  // const baseURL = 'https://solly-backend-staging.1qo1qzj3a6fc.ca-tor.codeengine.appdomain.cloud/'

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const response = await fetch(baseURL + 'token')
        if (!response.ok) throw new Error(`Failed to fetch token. Status: ${response.status}`)
        const data = await response.json()
        return data.token
      } catch (error) {
        console.error('Error fetching token:', error)
        throw error
      }
    }

    const fetchCarouselData = async (token) => {
      try {
        setLoading(true)
        const chatHistory = instance.createHistoryObject()

        const response = await fetch(baseURL + 'carousel', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-token': token,
          },
          body: JSON.stringify(chatHistory),
        })

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

        const result = await response.json()

        const processedItems = result.carousel_data.map((item) => ({
          ...item,
          isVideo:
            item.url.toLowerCase().includes('mp4') || item.url.toLowerCase().includes('stream'),
        }))

        setCarouselItems(processedItems)
      } catch (error) {
        console.error('Error fetching carousel data:', error)
      } finally {
        setLoading(false)
      }
    }

    const fetchData = async () => {
      try {
        const token = await fetchToken()
        await fetchCarouselData(token)
      } catch (error) {
        console.error('Error during data fetching:', error)
      }
    }
    fetchData()
  }, [BASE_URL, instance])

  const openModal = (index) => {
    setInitialModalIndex(index)
    setModalOpen(true)
  }

  const spinnerStyle = {
    border: '3px solid rgba(255, 255, 255, 0.3)',
    borderTop: '3px solid #ffffff',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    animation: 'spin 1.5s linear infinite',
  }

  const spinnerOverlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'left',
    justifyContent: 'left',
    zIndex: 10,
  }

  return (
    <div
      className="ChatContainer"
      style={{ position: 'relative', marginTop: '1rem', marginBottom: '1rem', minHeight: '30px' }}
    >
      {loading && (
        <div style={spinnerOverlayStyle}>
          <div style={spinnerStyle}></div>
        </div>
      )}
      {!loading && carouselItems.length > 0 && (
        <Swiper
          modules={[Navigation, Pagination, A11y, Mousewheel]}
          keyboard={{ enabled: true }}
          mousewheel={{ forceToAxis: true }}
          slidesPerView="auto"
          spaceBetween={15}
          centeredSlides
          rewind
          pagination={{
            el: navigationElement?.querySelector('.Carousel__BulletContainer') || null,
            clickable: true,
            bulletClass: 'Carousel__Bullet',
            bulletActiveClass: 'Carousel__Bullet--selected',
            renderBullet,
          }}
          navigation={{
            prevEl: navigationElement?.querySelector('button:nth-of-type(1)') || null,
            nextEl: navigationElement?.querySelector('button:nth-of-type(2)') || null,
          }}
        >
          {carouselItems.map((cardData, index) => {
            const { url, title, alt, isVideo } = cardData
            return (
              <SwiperSlide key={url}>
                <Tile className="Carousel__Card">
                  {!isVideo && (
                    <img
                      className="Carousel__CardImage"
                      src={url}
                      alt={alt}
                      style={{ cursor: 'pointer' }}
                      onClick={() => openModal(index)}
                    />
                  )}
                  {isVideo && (
                    <div
                      className="Carousel__VideoContainer"
                      style={{ position: 'relative' }}
                    >
                      <video
                        ref={(el) => (videoRefs.current[index] = el)}
                        className="Carousel__Video"
                        style={{
                          width: '100px',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          marginLeft: '-150px',
                        }}
                        muted
                        loop
                        autoPlay={!isPhoneScreen}
                        playsInline
                        onClick={() => openModal(index)} // Opens the modal when clicked
                      >
                        <source
                          src={url}
                          type="video/mp4"
                        />
                        Your browser does not support the video tag.
                      </video>
                      <img
                        src={playButton}
                        alt="Debug Play Button"
                        style={{
                          width: '50px',
                          height: '50px',
                          display: 'block',
                          position: 'absolute',
                          transform: 'translate(-245%, -140%)',
                        }}
                        onClick={() => openModal(index)}
                      />
                    </div>
                  )}
                </Tile>
              </SwiperSlide>
            )
          })}
        </Swiper>
      )}

      {!loading && carouselItems.length > 1 && (
        <div
          ref={setNavigationElement}
          className={cx('Carousel__Navigation')}
        >
          <button className="Carousel__NavigationButton">
            <CaretLeft size={16} />
          </button>
          <div className="Carousel__BulletContainer" />
          <button className="Carousel__NavigationButton">
            <CaretRight size={16} />
          </button>
        </div>
      )}

      {modalOpen && (
        <ImageModal
          images={carouselItems}
          initialIndex={initialModalIndex}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  )
}

function renderBullet(_, className) {
  return `<span class="${className}"></span>`
}

export { ContentCarouselGallery }
